import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TOKEN_SERVICE } from '@mhe/ol-platform/auth';
import * as readerConfigQuery from '@mhe/reader/core/state/configuration/configuration.selectors';
import { Store, select } from '@ngrx/store';
import { EMPTY, Observable, Subscription, combineLatest } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import * as readerSessionQuery from '@mhe/reader/state/session/session.selectors';
import * as configQuery from '@mhe/reader/state/config/config.selectors';
import { ReaderTokenService } from '@mhe/reader/tokens';
import { GoogleAnalyticsService } from '@mhe/reader/features/analytics';
import { ReaderConfigStore, ReaderStore } from '../reader/state';
import { Session } from '@mhe/reader/models';

// TODO Is this component deprecated????
@Component({
  selector: 'reader-ui-epub-ui-viewer',
  templateUrl: './epub-ui-viewer.component.html',
})
export class EpubUiViewerComponent implements OnInit, OnDestroy {
  readonly config$ = this.store.pipe(select(readerConfigQuery.configState));

  // route data
  readonly id$ = this.route.params.pipe(map((params) => params.id));
  readonly spine$ = this.route.params.pipe(map((params) => params.spine));
  readonly hash$ = this.route.fragment.pipe(map((f) => (f ? `#${f}` : f)));

  readonly url$ = (id: string): Observable<string> =>
    this.store.pipe(select(configQuery.getEpubUrlById, { id }));

  readonly epubUrl$ = this.id$.pipe(
    switchMap((id) => this.url$(id)),
    tap((url) => {
      if (!url) {
        throw new Error('Epub not found in LTI parameters');
      }
    }),
    catchError((error) => {
      console.error(error);
      void this.router.navigate(['/not-found'], { skipLocationChange: true });
      return EMPTY;
    }),
  );

  readonly hasSession$ = this.store.pipe(select(readerSessionQuery.hasSession));

  readonly timeoutDate$ = this.store.pipe(
    select(readerSessionQuery.getExpirationDate),
  );

  readonly showTimeoutModalFor$ = this.store.pipe(
    select(readerSessionQuery.getWarningThreshold),
    map((showFor) => showFor / 1000),
  );

  readonly aiAssistIsEnabled$ = combineLatest([
    this.readerConfigStore.isAiAssistEnabled$,
    this.readerStore.isAiAssistEnabled$,
    this.readerStore.isFactoryMetadataAiAssistEnabled$,
  ]).pipe(
    map(([configEnabled, storeEnabled, factoryEnabled]) => configEnabled && storeEnabled && factoryEnabled),
  );

  readonly showAiAssist$ = combineLatest([
    this.readerStore.showAiAssist$,
    this.aiAssistIsEnabled$,
  ]).pipe(
    map(([show, isEnabled]) => show && isEnabled),
  );

  private session: Partial<Session> | null;
  private timeoutDate: Date | undefined;
  private sessionStartTimestamp: number | null;
  private session$: Subscription;

  constructor(
    private readonly ga: GoogleAnalyticsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    private readonly readerStore: ReaderStore,
    private readonly readerConfigStore: ReaderConfigStore,
    @Inject(TOKEN_SERVICE) private readonly tokenService: ReaderTokenService,
  ) {}

  ngOnInit(): void {
    this.session$ = combineLatest([
      this.store.select(readerSessionQuery.getSession),
      this.store.select(readerSessionQuery.getSessionStartTimestamp),
      this.store.select(readerSessionQuery.getExpirationDate),
    ]).subscribe(([session, sessionStartTimestamp, expirationDate]) => {
      this.session = session;
      this.sessionStartTimestamp = sessionStartTimestamp;
      this.timeoutDate = expirationDate;
    });
    // Add global sessionInformation function
    (window as any).sessionInformation = () => {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userTime = new Date().toLocaleString('en-US', { timeZone: userTimeZone });

      let serverTime: string | undefined;
      let timeLeftSeconds: string | undefined;
      if (this.session?.issuedAt && this.sessionStartTimestamp) {
        // issuedAt is usually in seconds, convert to ms
        serverTime = new Date(this.session.issuedAt).toLocaleString('en-US', { timeZone: 'UTC' });
      }

      if (this.session?.sessionTimeout && this.sessionStartTimestamp) {
        const now = Date.now();
        timeLeftSeconds = Math.max(0, Math.floor(((this.timeoutDate as Date).getTime() - now) / 1000)).toString();
      }
      console.log('--- Session Information ---');
      console.log('Session object:', this.session);
      console.log('Session start timestamp:', this.sessionStartTimestamp);
      console.log('Local set time:', userTime, 'User Timezone:', userTimeZone);
      console.log('Server sent time:', serverTime, 'UTC');
      console.log('Timeout counter:', timeLeftSeconds);
      console.log('Timeout date:', (this.timeoutDate as Date ?? new Date()).toLocaleString('en-US'));
      console.log('--------------------------');
    };
  }

  ngOnDestroy(): void {
    this.session$.unsubscribe();
  }

  refreshSession(): void {
    this.tokenService
      .refreshToken()
      .pipe(tap((response) => this.tokenService.setToken(response)))
      .subscribe();
  }

  endSession(): void {
    void this.router.navigateByUrl('401', { skipLocationChange: true });
  }

  onModalToggle(open: boolean): void {
    if (open) {
      this.ga.event({
        eventCategory: 'Dialog',
        eventAction: 'Session Timeout',
      });
    }
  }
}
